import React, { useState } from 'react';
import {Table, Input, Button, Modal, notification} from 'antd';
import TaxAssistantModal from './TaxAssistantModal';

const SplitDetailsTable = ({ setSplitDetails, splitDetails, zoopPlans, mode, enableEdit }) => {
  const parseJSON = (jsonString) => {
    try {
      const validJsonString = jsonString.replace(/'/g, '"');
      return JSON.parse(validJsonString);
    } catch (e) {
      console.error("Invalid split details:", jsonString);
      return {};
    }
  };

  const getPaymentTypes = () => {
    if (mode === 'physical' || mode === 'online') {
      return ['debit', 'credit']
    } else if(mode === 'others') {
      return ['debit']
    }
  }

  const getFees = () => {
    let fees = zoopPlans.details.fees;
    let filteredFees = {};
    for (let paymentType in fees) {
      if (mode !== 'others' && paymentType !== 'credit' && paymentType !== 'debit') {
        continue
      }
      let paymentFees = {}
      for (let installment in fees[paymentType]) {
        let installmentFees = {};
        for (let brand in fees[paymentType][installment]) {
          let brandFees = []
          for (let fee of fees[paymentType][installment][brand]) {
            if (fee['capture_mode'] == null && mode === 'online') {  // fee is physical, but online was requested
              continue
            }
            if (fee['capture_mode'] === "manually_keyed" && mode === 'physical') {  // fee is online, but physical was requested
              continue
            }
            brandFees.push(fee);
          }
          if (brandFees.length > 0) {
            installmentFees[brand] = brandFees;
          }
        }
        paymentFees[installment] = installmentFees;
      }
      filteredFees[paymentType] = paymentFees;
    }
    return filteredFees;
  }

  const getBrands = () => {
    if(mode ==='others') {
      return ['PIX', 'Boleto'];
    }
    let fees = getFees();
    let brands = [];
    for (let paymentType in fees) {
      for (let installment in fees[paymentType]) {
        for (let brand in fees[paymentType][installment]) {
          if (!brands.includes(brand)) {
            brands.push(brand);
          }
        }
      }
    }
    // ensure that if present, MasterCard is the first of the list, followed by Visa and Elo
    if (brands.includes('Elo')) {
      brands = ['Elo', ...brands.filter(b => b !== 'Elo')];
    }
    if (brands.includes('Visa')) {
      brands = ['Visa', ...brands.filter(b => b !== 'Visa')];
    }
    if (brands.includes('MasterCard')) {
      brands = ['MasterCard', ...brands.filter(b => b !== 'MasterCard')];
    }
    return brands;
  }

  const initializeData = (splitDetails, zoopPlans) => {
    const initialData = typeof splitDetails === 'string' ? parseJSON(splitDetails) : splitDetails;

    const paymentTypes = getPaymentTypes();
    const installments = {
      credit: Object.keys(getFees().credit || {}),
      debit: ['1'] 
    };
    const brands = getBrands();
    const receivers = ['Administrador', 'Agente', 'Distribuidor N1', 'Distribuidor N2', 'Programador'];

    paymentTypes.forEach(paymentType => {
      if (!initialData.details) initialData.details = {};
      if (!initialData.details.fees) initialData.details.fees = {};
      if (!initialData.details.fees[paymentType]) initialData.details.fees[paymentType] = {};

      installments[paymentType].forEach(installment => {
        if (!initialData.details.fees[paymentType][installment]) initialData.details.fees[paymentType][installment] = {};

        brands.forEach(brand => {
          if (!initialData.details.fees[paymentType][installment][brand]) initialData.details.fees[paymentType][installment][brand] = {};

          receivers.forEach(receiver => {
            if (initialData.details.fees[paymentType][installment][brand][receiver] === undefined) {
              initialData.details.fees[paymentType][installment][brand][receiver] = 0;
            }
            let fee = 0;
            if (initialData[paymentType] && initialData[paymentType][installment] && initialData[paymentType][installment][brand] && initialData[paymentType][installment][brand][receiver]) {
              fee = initialData[paymentType][installment][brand][receiver];
            }
            initialData.details.fees[paymentType][installment][brand][receiver] = fee
          });
        });
      });
    });

    return initialData;
  };

  // clear keys with value 0
  const compactDict = (dict) => {
    let newDict = {}
    for (let key in dict) {
      if (dict[key] !== 0 && dict[key] !== null) {
        if (typeof dict[key] === 'object') {
          let compact = compactDict(dict[key])
          if (Object.keys(compact).length > 0) {
            newDict[key] = compact
          }
          continue
        }
        newDict[key] = dict[key]
      }
    }

    return newDict
  }

  const [data, setData] = useState(initializeData(splitDetails, zoopPlans));
  const [modalVisible, setModalVisible] = useState(false);
  const [editingValues, setEditingValues] = useState({});
  const handleInputChange = (value, paymentType, installments, brand, receiver) => {
    let newValue = parseFloat(value.replace(',', '.'));
    if (isNaN(newValue)) newValue = 0;

    const newData = { ...data };
    if (!newData.details) newData.details = {};
    if (!newData.details.fees) newData.details.fees = {};
    if (!newData.details.fees[paymentType]) newData.details.fees[paymentType] = {};
    if (!newData.details.fees[paymentType][installments]) newData.details.fees[paymentType][installments] = {};
    if (!newData.details.fees[paymentType][installments][brand]) newData.details.fees[paymentType][installments][brand] = {};
    newData.details.fees[paymentType][installments][brand][receiver] = newValue;

    setEditingValues((prev) => ({
      ...prev,
      [`${paymentType}-${installments}-${brand}-${receiver}`]: newValue,
    }));

    setData(newData);
    setSplitDetails(JSON.stringify(compactDict(newData?.details?.fees)))
  };

  const handleInputFocus = (paymentType, installments, brand, receiver, value) => {
    setEditingValues((prev) => ({
      ...prev,
      [`${paymentType}-${installments}-${brand}-${receiver}`]: value,
    }));
  };

  const getRowData = (paymentType, installments, brand, receiver) => {
    const key = `${paymentType}-${installments}-${brand}-${receiver}`;
    const value =
      editingValues[key] !== undefined
        ? editingValues[key]
        : data.details &&
          data.details.fees &&
          data.details.fees[paymentType] &&
          data.details.fees[paymentType][installments] &&
          data.details.fees[paymentType][installments][brand] &&
          data.details.fees[paymentType][installments][brand][receiver] !== undefined
          ? data.details.fees[paymentType][installments][brand][receiver]
          : 0;

    return (
      <Input
        value={value.toString()}
        onChange={(e) => handleInputFocus(paymentType, installments, brand, receiver, e.target.value)}
        onBlur={(e) => handleInputChange(e.target.value, paymentType, installments, brand, receiver)}
        disabled={!enableEdit}
      />
    );
  };

  const getFinalValue = (paymentType, installments, brand) => {
    let zoopTax = 0;
    let receiversSum = 0;

    if (
      data.details &&
      data.details.fees &&
      data.details.fees[paymentType] &&
      data.details.fees[paymentType][installments] &&
      data.details.fees[paymentType][installments][brand]
    ) {
      for (let key of Object.keys(data.details.fees[paymentType][installments][brand])) {
        receiversSum += data.details.fees[paymentType][installments][brand][key];
      }
    }

    let isPercentage = true;
    if (brand === 'PIX') {
      paymentType = 'pix'
      brand = null
    } else if (brand === 'Boleto') {
      paymentType = 'boleto'
      brand = null
    }
    const planData = getFees();
    if (
      planData[paymentType] &&
      planData[paymentType][installments] &&
      planData[paymentType][installments][brand] &&
      planData[paymentType][installments][brand][0]
    ) {
      if (planData[paymentType][installments][brand][0].dollar_amount > 0) {
        isPercentage = false;
        zoopTax = planData[paymentType][installments][brand][0].dollar_amount / 100;
      } else {
        zoopTax = planData[paymentType][installments][brand][0].percent_amount / 100;
      }
    }

    return (isPercentage ? '' : 'R$ ') + (receiversSum + zoopTax).toFixed(2);
  };

  const handleOk = (paymentType, selectedInstallments, receiver, tax, sameTax = false) => {
    const installmentsList = selectedInstallments === 'all'
      ? (paymentType === 'debit' ? ['1'] : Object.keys(getFees().credit || {}))
      : [selectedInstallments];

    const newData = { ...data };
    if (!newData.details) newData.details = {};
    if (!newData.details.fees) newData.details.fees = {};
    if (!newData.details.fees[paymentType]) newData.details.fees[paymentType] = {};

    const allBrands = getBrands();

    if (sameTax) {
      let requiresReduction = false;
      let cannotMeetFinalTax = false;

      for (let installments of installmentsList) {
        if (!newData.details.fees[paymentType][installments]) newData.details.fees[paymentType][installments] = {};
        for (let brand of allBrands) {
          if (!newData.details.fees[paymentType][installments][brand]) {
            newData.details.fees[paymentType][installments][brand] = {};
          }
          let currentFinalValue = getFinalValue(paymentType, installments, brand);
          const currentTax = newData.details.fees[paymentType][installments][brand][receiver] || 0;
          const additionalTax = tax - (currentFinalValue - currentTax);

          if (additionalTax < currentTax) {
            requiresReduction = true;
          }

          if (additionalTax < 0) {
            cannotMeetFinalTax = true;
          }
        }
      }

      if (cannotMeetFinalTax) {
        Modal.confirm({
          title: 'Confirmação de Limite',
          content: `Para atingir a taxa final de ${tax}%, em alguns casos zerar a taxa do recebedor (${receiver}) não será o suficiente. O que deseja fazer?`,
          okText: 'Somente alterar onde for possível',
          cancelText: 'Cancelar operação',
          onOk() {
            for (let installments of installmentsList) {
              applyFinalTax(newData, paymentType, installments, receiver, tax, allBrands, true);
            }
          },
        });
      } else if (requiresReduction) {
        Modal.confirm({
          title: 'Confirmação de Redução',
          content: `Para atingir a taxa final de ${tax}%, o ganho do recebedor (${receiver}) precisará ser reduzido em alguns casos. Prosseguir com a alteração?`,
          onOk() {
            for (let installments of installmentsList) {
              applyFinalTax(newData, paymentType, installments, receiver, tax, allBrands);
            }
          },
        });
      } else {
        for (let installments of installmentsList) {
          applyFinalTax(newData, paymentType, installments, receiver, tax, allBrands);
        }
      }
    } else {
      for (let installments of installmentsList) {
        if (!newData.details.fees[paymentType][installments]) newData.details.fees[paymentType][installments] = {};
        for (let brand of allBrands) {
          if (!newData.details.fees[paymentType][installments][brand]) {
            newData.details.fees[paymentType][installments][brand] = {};
          }
          newData.details.fees[paymentType][installments][brand][receiver] = tax;
        }
      }
      notification.success({
        message: 'Sucesso',
        description: 'Taxa aplicada com sucesso',
      })
      setModalVisible(false);
    }

    setData(newData);
    setSplitDetails(JSON.stringify(compactDict(newData?.details?.fees)))
    setEditingValues({});
  };

  const applyFinalTax = (newData, paymentType, installment, receiver, tax, allBrands, partial = false) => {
    if (!newData.details.fees[paymentType][installment]) {
      newData.details.fees[paymentType][installment] = {};
    }
    for (let brand of allBrands) {
      if (!newData.details.fees[paymentType][installment][brand]) {
        newData.details.fees[paymentType][installment][brand] = {};
      }
      let currentFinalValue = getFinalValue(paymentType, installment, brand);
      const currentTax = newData.details.fees[paymentType][installment][brand][receiver] || 0;
      const additionalTax = tax - (currentFinalValue - currentTax);

      if (partial && additionalTax < 0) {
        newData.details.fees[paymentType][installment][brand][receiver] = 0;
      } else if (additionalTax < 0) {
        newData.details.fees[paymentType][installment][brand][receiver] = parseFloat(additionalTax.toFixed(2));
      } else {
        newData.details.fees[paymentType][installment][brand][receiver] = parseFloat(additionalTax.toFixed(2));
      }
    }
    setData(newData);
    setSplitDetails(JSON.stringify(compactDict(newData?.details?.fees)))
    notification.success({
      message: 'Sucesso',
      description: 'Taxa final aplicada com sucesso',
    })
    setModalVisible(false);
  };

  const installmentsOptions = [
    ...new Set([
      ...Object.keys(getFees().credit || {}),
      ...Object.keys(getFees().debit || {}),
    ]),
  ];

  const columns = [
    {
      title: 'Tipo',
      dataIndex: 'type',
      key: 'type',
      fixed: 'left'
    },
    {
      title: '',
      dataIndex: 'installments',
      key: 'installments',
      fixed: 'left'
    },
    ...getBrands().map(brand => ({
      title: brand,
      dataIndex: brand,
      key: brand
    })),
  ];

  const brandsWithManual = getBrands();

  const installments = {
    credit: Object.keys(getFees().credit || {}),
    debit: ['1'] 
  };

  const getBaseTax = (paymentType, installment, brand) => {
    if (brand === 'PIX') {
      paymentType = 'pix'
      brand = null
    } else if (brand === 'Boleto') {
      paymentType = 'boleto'
      brand = null
    }
    let zoopTax = 0;
    let fees = getFees();
    if (!fees[paymentType] || !fees[paymentType][installment] || !fees[paymentType][installment][brand]) {
      return '0.00';
    }

    if (fees[paymentType][installment][brand][0].dollar_amount > 0) {
      zoopTax = fees[paymentType][installment][brand][0].dollar_amount
    } else {
      zoopTax = fees[paymentType][installment][brand][0].percent_amount
    }

    return (zoopTax / 100).toFixed(2)
  }

  const dataSource = getPaymentTypes().map((paymentType, typeIndex) => {
    const paymentTypeName = paymentType === 'credit' ? 'Crédito' : 'Débito';

    return {
      key: typeIndex,
      type: paymentTypeName,
      installments: '',
      children: installments[paymentType].map((installment, instIndex) => {
        return {
          key: `${typeIndex}-${instIndex}`,
          type: '',
          installments: `${installment}x`,
          ...brandsWithManual.reduce((acc, brand) => {
            acc[brand] = getFinalValue(paymentType, installment, brand);
            return acc;
          }, {}),
          children: [
            {
              key: `${typeIndex}-${instIndex}-base`,
              type: 'Taxa Base',
              installments: '',
              ...brandsWithManual.reduce((acc, brand) => {
                acc[brand] = getBaseTax(paymentType, installment, brand);
                return acc;
              }, {})
            },
            ...['Administrador', 'Agente', 'Distribuidor N1', 'Distribuidor N2', 'Programador'].map((receiver, recIndex) => ({
              key: `${typeIndex}-${instIndex}-${recIndex}`,
              type: receiver,
              installments: '',
              ...brandsWithManual.reduce((acc, brand) => {
                acc[brand] = getRowData(paymentType, installment, brand, receiver);
                return acc;
              }, {})
            }))
          ]
        };
      })
    };
  });
  return (
    <>
    {getBrands().length > 0 && (
      <>
        {mode !== 'others' && (
          <>
            <Button style={{marginBottom: "30px"}} type="primary" onClick={() => setModalVisible(true)} disabled={!enableEdit}>Assistente de taxas</Button>
            <TaxAssistantModal
              visible={modalVisible}
              onCancel={() => setModalVisible(false)}
              onOk={handleOk}
              installmentsOptions={installmentsOptions}
            />
          </>
        )}
        <Table
          className='splitTable'
          columns={columns}
          dataSource={dataSource}
          pagination={false}
          scroll={{ x: 'max-content', y: 800 }}
          sticky
        />
      </>
    )}
    {getBrands().length === 0 && (
      <>
        <p> Este plano não possui suporte a vendas débito/crédito para {mode === 'online' ? "Online" : "Presencial"}</p>
      </>
    )}
    </>
  );
};

export default SplitDetailsTable;
